/**
 * RenewalCliffBadge (review cliff badge)
 *
 * Small badge showing upcoming review cliff - works for both
 * retainer renewals (agency) and budget review cycles (in-house).
 * - Orange: < 14 days
 * - Red: < 7 days
 * Hidden if no review date (daysUntilRenewal === null) or > 30 days.
 */

import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/ui/Tooltip';
import type { RenewalData } from '@/data/portfolio.types';

interface RenewalCliffBadgeProps {
  renewal: RenewalData;
}

export const RenewalCliffBadge = ({ renewal }: RenewalCliffBadgeProps) => {
  const { t } = useTranslation('agency');
  const { daysUntilRenewal } = renewal;

  if (daysUntilRenewal === null || daysUntilRenewal >= 30) {
    return null;
  }

  const isUrgent = daysUntilRenewal < 7;
  const colorClasses = isUrgent
    ? 'bg-coral-50 text-coral-700 border border-coral-200'
    : 'bg-peach/30 text-coral-700 border border-peach';

  return (
    <Tooltip content={t('portfolio_tooltip_review_cliff', { defaultValue: 'Next review in {{days}} days. Surface the conversation now.', days: daysUntilRenewal })} side="top" delayDuration={300}>
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${colorClasses} cursor-default`}
        role="status"
        aria-label={t('portfolio_renewal_aria', { count: daysUntilRenewal })}
      >
        <i className="hm hm-calendar" style={{ fontSize: 9 }} aria-hidden="true" />
        {t('portfolio_renewal_days_short', { days: daysUntilRenewal })}
      </span>
    </Tooltip>
  );
};
